import { PROJECT_CLIENT_FIELDS, PROJECT_FIELDS } from "./constants";
import { useEffect, useMemo, useRef } from "react";

import { fetchProjectDataforForm } from "@/modules/work/projects/project/crud/form/read";
import { getRequiredFields } from "components/form/utils";
import { projectSchema } from "./schema";
import slugify from "slugify";
import useCheckPermissions from "hooks/src/useCheckPermissons";
import { useCreateProject } from "@/modules/work/projects/project/crud/create/mutation";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useUpdateProject } from "@/modules/work/projects/project/crud/update/mutation";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import { useState } from "react";

export const useProjectForm = ({ projectId }) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [isSalesRole, setIsSalesRole] = useState(null);
  // country id -> projects__countries__countries junction row id, as loaded from the server
  const countryJunctionsRef = useRef(new Map());

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();

  const { canRead: canReadClient } = useCheckPermissions({
    collection: "clients",
  });

  useEffect(() => {
    const func = user.role.name.split("-")[0];
    if (func === "SLS") {
      setIsSalesRole(true);
    }
  }, [user]);

  const dynamicSchema = useMemo(() => {
    if (projectId && !canReadClient) {
      return projectSchema.extend({
        client: z.optional(z.string()),
      });
    }
    return projectSchema;
  }, [projectId, canReadClient]);

  const form = useForm({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      logo: "",
      name: "",
      sales_coordinator: isSalesRole ? user : null,
      project_id: "",
      status: "active",
      description: "",
      alias: "",
      industry: null,
      countries: [],
      start_date: null,
      end_date: null,
      about: "",
      statistics: [],
    },
  });

  useEffect(() => {
    if (user && isSalesRole) {
      form.reset({
        ...form.getValues(),
        sales_coordinator: isSalesRole ? user : null,
      });
    }
  }, [user, isSalesRole]);

  const requiredFields = useMemo(
    () => getRequiredFields(dynamicSchema),
    [dynamicSchema],
  );

  const name = form.watch("name");

  useEffect(() => {
    if (!projectId) {
      if (name) {
        const slug = slugify(name, { lower: true, strict: true });
        form.setValue("project_id", slug);
      } else {
        form.setValue("project_id", "");
      }
    }
  }, [name, projectId]);

  async function handleCreate(context) {
    const values = context.values;
    const response = await createProjectMutation.mutateAsync({
      values,
    });
    navigate(`/work/projects/${response.project_id}`);
  }

  async function handleRead(id) {
    const fields = [...PROJECT_FIELDS];

    if (canReadClient) {
      fields.push(PROJECT_CLIENT_FIELDS);
    }

    const response = await fetchProjectDataforForm({ id, fields });



    const tags = Array.isArray(response.tags)
      ? response.tags.map((t) => t.tag.id)
      : [];

    const junctions = new Map();
    (Array.isArray(response.countries) ? response.countries : []).forEach(
      (row) => {
        const countryId = row?.country?.id ?? row?.country;
        if (countryId) junctions.set(countryId, row.id);
      }
    );
    countryJunctionsRef.current = junctions;
    const countries = [...junctions.keys()];

    const data = {
      ...response,
      tags,
      countries,
      alias: response.alias || "",
      industry: response.industry?.id ?? response.industry ?? null,
      start_date: response.start_date || null,
      end_date: response.end_date || null,
      about: response.about || "",
      statistics: Array.isArray(response.statistics)
        ? response.statistics.map((s) => ({
            label: s?.label ?? "",
            value: s?.value != null ? String(s.value) : "",
          }))
        : [],
      legal_entity: response.legal_entity?.id || "",
      description: response.description || "",
    };

    console.log("data",data);
    

    return data;
  }

  async function handleUpdate(id, context) {
    const values = { ...context.dirtyValues };
    if ("countries" in values) {
      values.countries = buildCountriesChange(
        values.countries,
        countryJunctionsRef.current
      );
    }
    const response = await updateProjectMutation.mutateAsync({
      projectId: id,
      values,
    });

    navigate(-1);
  }

  return {
    form,
    isSalesRole,

    requiredFields,
    canReadClient,
    handleCreate,
    handleRead,
    handleUpdate,
  };
};

// Directus M2M change set: only add new countries and remove deselected
// junction rows, leaving untouched rows (and their ids) in place.
function buildCountriesChange(selected, existingJunctions) {
  const selectedIds = new Set(
    (Array.isArray(selected) ? selected : [])
      .map((c) => (typeof c === "object" ? c?.id : c))
      .filter(Boolean)
  );
  const create = [...selectedIds]
    .filter((id) => !existingJunctions.has(id))
    .map((id) => ({ country: id }));
  const remove = [...existingJunctions.entries()]
    .filter(([countryId]) => !selectedIds.has(countryId))
    .map(([, junctionId]) => junctionId);
  return { create, update: [], delete: remove };
}
